import * as THREE from 'three';
import { hustleOpen } from '../content/hustles';
import type { Choice } from '../content/types';
import type { NpcAgent } from '../entities/NpcManager';
import { usedToday, useToday } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import { LOTS, SPOTS, type Vec2 } from '../world/layout';
import { roomSlotAt } from '../world/rooms';
import type { Activities } from './Activities';
import type { GameServices } from './services';

export const DELIVERY = { runsPerDay: 3, stops: 3, opens: 10, closes: 23, base: 6, maxTip: 9 } as const;
/** Real seconds until the food's stone cold. */
const COLD_SECONDS = 150;
const FAR = { x: 1e6, z: 1e6 };

interface Drop {
  who: string;
  /** Where to knock, and what to call the place. */
  at: Vec2;
  place: string;
  /** What they say when the food shows up hot, and cold. */
  hot: string;
  cold: string;
}

/** Everybody who orders out (the Gas-N-Grub has a hot case, and a guy with a car is all it takes). */
export const DROPS: readonly Drop[] = [
  {
    who: 'Big Earl',
    at: LOTS.earl.stoop,
    place: 'Lot 6',
    hot: "'Still steamin'! You're a hero.'",
    cold: "'It's cold, but so's my beer. Evens out.'",
  },
  {
    who: 'Tammy Lynn',
    at: LOTS.tammy.stoop,
    place: 'Lot 5',
    hot: "'Perfect timing, my Live starts in five.'",
    cold: "'Cold? I'll put it on my story anyway. #blessed'",
  },
  {
    who: 'Cousin Dale',
    at: LOTS.dale.stoop,
    place: 'Lot 8',
    hot: "'Tip's in the bag. Don't look in the bag. Just the tip part.'",
    cold: "'Cold pizza's breakfast food anyway. Wait, what time is it?'",
  },
  {
    who: 'Old Man Jenkins',
    at: LOTS.jenkins.stoop,
    place: 'Lot 9',
    hot: "'Hot food. First time since the Carter administration.'",
    cold: "'Cold. Like my late wife's feet. I miss them feet.'",
  },
  {
    who: 'Skeeter',
    at: LOTS.skeeter.stoop,
    place: 'Lot 11',
    hot: "'Bro, that was FAST. Can I film you leaving?'",
    cold: "'Bro it's cold. Still eating it though. Content.'",
  },
  {
    who: 'Brenda',
    at: LOTS.brenda.stoop,
    place: 'Lot 12',
    hot: "'Hot and on time? Honey, marry me.'",
    cold: "'Cold, but I been on my feet all day. I'll eat a shoe.'",
  },
  {
    who: 'Madame Wanda',
    at: LOTS.wanda.stoop,
    place: 'Lot 14',
    hot: "'I saw you coming. In the cards. Also out the window.'",
    cold: "'The spirits said it'd be cold. The spirits were right.'",
  },
  {
    who: 'Hank',
    at: LOTS.hank.stoop,
    place: 'Lot 15',
    hot: "'Leave it on the mat and walk away. ...Thank you.'",
    cold: "'Cold. They intercepted it. Typical.'",
  },
  {
    who: 'Meemaw Pearl',
    at: SPOTS.meemawStoop,
    place: 'Possum Trot',
    hot: "'Oh, bless you, it's still warm. Here, take somethin' for your trouble.'",
    cold: "'Cold. Well. I'll microwave it. I'll microwave you too if you're late again.'",
  },
  {
    who: 'Darlene',
    at: SPOTS.office,
    place: 'the park office',
    hot: "'Hot food at the office? Don't tell Apex. They'll charge for it.'",
    cold: "'It's cold, sugar. Peanut don't care. Peanut, no.'",
  },
  {
    who: "Lil' Ricky",
    at: SPOTS.ricky,
    place: 'the Commons',
    hot: "'Fuel for the champ. Respect.'",
    cold: "'Cold food, cold streak. Thanks for nothin'. Here's your tip.'",
  },
  {
    who: 'Possum Pete',
    at: SPOTS.pete,
    place: 'the school bus',
    hot: "'Ain't nobody ever delivered to the bus before. I'm touched.'",
    cold: "'Cold? I been eatin' worse outta the crick.'",
  },
];

const ORDERS = [
  'a large pepperoni',
  'a 12-piece bucket',
  'six roller dogs',
  'a 2-liter of Mtn Dewd and a carton of Paul Mauls',
  'gas station sushi (they asked for it by name)',
  'nachos with the pump cheese',
  'a bag of ice and three Fire Bawls',
  'two Hungry-Man XLs, nuked',
];

/** The tip for food that's still `heat`% hot (0..100). */
export function deliveryTip(heat: number): number {
  return Math.round((Math.min(100, Math.max(0, heat)) / 100) * DELIVERY.maxTip);
}

interface Run {
  stops: Array<Drop & { order: string; done: boolean }>;
  started: number;
  earned: number;
  /** The game clock last frame: a big jump means you slept or passed out. */
  clock: number;
}

/**
 * Hot Case Delivery: Raylene hands you three orders from the Gas-N-Grub's hot case.
 * Knock on three doors around the park. Every stop pays, and the tip shrinks as the food
 * goes cold, so it pays to hurry (a car or the Lil' Rascal helps).
 */
export class Deliveries {
  private run: Run | null = null;
  private readonly tmp = new THREE.Vector3();

  constructor(
    private readonly s: GameServices,
    act: Activities,
  ) {
    act.talkChoices.push((agent) => this.offer(agent));
  }

  get active(): boolean {
    return this.run !== null;
  }

  /** 0..100: how hot the food still is. */
  heat(): number {
    if (!this.run) return 0;
    return Math.max(0, 100 - ((this.s.now() - this.run.started) / COLD_SECONDS) * 100);
  }

  register(interactions: Interactions): void {
    for (let i = 0; i < DELIVERY.stops; i++) {
      interactions.add({
        id: `delivery-${i}`,
        at: () => {
          const stop = this.run?.stops[i];
          return stop && !stop.done ? stop.at : FAR;
        },
        radius: 2.4,
        priority: 1.5,
        prompt: () => {
          const stop = this.run?.stops[i];
          return stop && !stop.done ? `Knock: delivery for ${stop.who}` : null;
        },
        action: () => this.deliver(i),
        icon: ':pizza:',
      });
    }
  }

  /**
   * New game, a load, or back to the title. A run in progress isn't saved, so if the save
   * says one was underway, you get that day's run back.
   */
  reset(): void {
    this.run = null;
    const { store } = this.s;
    const active = store.flag('delivery_active');
    if (typeof active !== 'number') return;
    if (active === store.day)
      store.setFlag('delivery_runs', `${store.day}:${Math.max(0, usedToday(store, 'delivery_runs') - 1)}`);
    store.setFlag('delivery_active', false);
  }

  /** The run's over (all delivered, or it fell apart). */
  private end(): void {
    this.run = null;
    this.s.store.setFlag('delivery_active', false);
  }

  /** Raylene's got a run for you (once it's unlocked). */
  private offer(agent: NpcAgent): Choice[] {
    if (agent.def.id !== 'raylene') return [];
    const { store } = this.s;
    if (!hustleOpen(store.state, 'delivery')) return [];
    const runs = usedToday(store, 'delivery_runs');
    return [
      {
        label: ':pizza: Run deliveries',
        hint: `${DELIVERY.stops} stops · $${DELIVERY.base}+ each`,
        requires: () => {
          const h = store.minutes / 60;
          if (this.run) return 'Already on a run';
          if (h < DELIVERY.opens || h >= DELIVERY.closes) return 'Hot case is 10 AM to 11 PM';
          if (runs >= DELIVERY.runsPerDay) return "That's all the orders today";
          return true;
        },
        run: () => this.start(),
      },
    ];
  }

  private start(): string {
    const { rng, store } = this.s;
    const stops = rng
      .shuffle(DROPS)
      .slice(0, DELIVERY.stops)
      .map((d) => ({ ...d, order: rng.pick(ORDERS), done: false }));
    this.run = { stops, started: this.s.now(), earned: 0, clock: store.now };
    useToday(store, 'delivery_runs');
    store.setFlag('delivery_active', store.day);
    const list = stops.map((s) => `• ${s.who} (${s.place}): ${s.order}`).join('\n');
    return `Raylene slides three bags through the drawer. 'Hot case delivery. Knock loud, Jenkins don't hear good. Food's hot for about two minutes, so's the tip.'\n\n${list}\n\nThe arrow points to the closest stop. Hurry.`;
  }

  private deliver(i: number): void {
    const run = this.run;
    const stop = run?.stops[i];
    if (!run || !stop || stop.done) return;
    const { store, ui } = this.s;
    const heat = this.heat();
    const tip = deliveryTip(heat);
    const pay = DELIVERY.base + tip;
    stop.done = true;
    run.earned += pay;
    store.apply({ money: pay });
    this.s.sfx('door');
    window.setTimeout(() => this.s.sfx('cash'), 250);
    const line = heat > 40 ? stop.hot : stop.cold;
    ui.hud.bubble(`delivery-${i}`, line.replace(/^'|'$/g, ''), this.tmp.set(stop.at.x, 2.4, stop.at.z), 4);
    store.toast(
      `${stop.who}: +$${pay} (${tip ? `$${tip} tip, food was ${Math.round(heat)}% hot` : 'no tip, it was cold'})`,
      tip ? 'good' : 'info',
      ':pizza:',
    );
    if (run.stops.every((s) => s.done)) {
      const bonus = heat > 50 ? 5 : 0;
      if (bonus) store.apply({ money: bonus });
      store.incr('deliveries');
      store.addClout(2);
      this.end();
      window.setTimeout(
        () =>
          store.toast(
            `Run done: $${run.earned + bonus}${bonus ? ` (with a $${bonus} hot-run bonus from Raylene)` : ''}. Raylene's got more orders later.`,
            'good',
            ':pizza:',
          ),
        600,
      );
    }
  }

  update(): void {
    // Sleeping, passing out (or it getting to be 2 AM) ends the run; the food goes in the dumpster.
    const run = this.run;
    if (!run) return;
    const { store } = this.s;
    const skipped = store.now - run.clock > 120;
    run.clock = store.now;
    const h = store.minutes / 60;
    if (skipped || (h >= 2 && h < 8)) {
      this.end();
      store.toast(
        'The delivery run fell apart. The food went in the dumpster. Raylene will understand. Raylene will not understand.',
        'bad',
        ':pizza:',
      );
    }
  }

  objective(): { text: string; target: THREE.Vector3 } | null {
    const run = this.run;
    if (!run) return null;
    const p = this.s.player.position;
    if (roomSlotAt(p.x) >= 0) return null;
    const left = run.stops.filter((s) => !s.done);
    const next = left.reduce((a, b) =>
      Math.hypot(a.at.x - p.x, a.at.z - p.z) <= Math.hypot(b.at.x - p.x, b.at.z - p.z) ? a : b,
    );
    const heat = Math.round(this.heat());
    return {
      text: `:pizza: ${next.who} (${next.place}) · ${left.length} left · ${heat > 0 ? `:fire:${heat}%` : ':ice: cold'}`,
      target: this.tmp.set(next.at.x, 0, next.at.z),
    };
  }
}
